import React, { useEffect, useRef, useState } from 'react';
import toast from 'react-hot-toast';
import {
  fetchPhoneAuthConfig,
  requestPhoneCode,
  phoneLogin,
  phoneRegister,
  phonePasswordLogin,
  phoneBind,
} from '../../utils/phoneAuthApi';

// Built-in English labels so the panel works with no i18n setup. A host that
// has translations can pass `i18n={(key) => translatedString}`.
const DEFAULT_LABELS = {
  orUsePhone: 'or use phone',
  phonePlaceholder: 'Phone number',
  phoneInvalid: 'Please enter a valid phone number.',
  sendCode: 'Send code',
  codeSent: 'Verification code sent.',
  codeSimulated: 'Dev mode: the code is logged on the server.',
  codeFailed: 'Could not send the code.',
  resendIn: 'Resend in {{s}}s',
  verificationCode: 'Verification code',
  codeRequired: 'Please enter the code.',
  invalidCode: 'Incorrect or expired code.',
  passwordOptional: 'Password (optional)',
  passwordRequired: 'Please enter your password.',
  invalidPassword: 'Incorrect phone or password.',
  signInWithCode: 'Sign in with code',
  signInWithPassword: 'Sign in with password',
  createAccount: 'Create account',
  bindPhone: 'Link phone number',
  phoneBound: 'Phone number linked.',
  signInRequired: 'Please sign in first.',
  bindFailed: 'Could not link this phone.',
  name: 'Name (optional)',
};

function persistSession(token, user, next) {
  if (!token) return;
  try {
    localStorage.setItem('authToken', token);
    if (user) localStorage.setItem('authUser', JSON.stringify(user));
  } catch {
    /* storage may be unavailable; ignore */
  }
  window.location.assign(next || (typeof window !== 'undefined' ? window.location.pathname : '/account'));
}

/**
 * Phone + SMS verification-code sign-in / sign-up panel.
 *
 * Modes:
 *   'login'    — passwordless code login, with an optional phone+password path
 *   'register' — code sign-up, with optional password + name
 *   'bind'     — (authenticated) attach a verified phone to the current account
 *
 * The phone tab is only rendered when the backend reports SMS as configured
 * (see fetchPhoneAuthConfig), so a missing provider degrades gracefully.
 *
 * @param {{ mode?: 'login'|'register'|'bind', next?: string, i18n?: Function, onSuccess?: Function }} props
 */
const PhoneAuthPanel = ({ mode = 'login', next, i18n, onSuccess }) => {
  const [phone, setPhone] = useState('');
  const [code, setCode] = useState('');
  const [password, setPassword] = useState('');
  const [name, setName] = useState('');
  const [cooldown, setCooldown] = useState(0);
  const [sending, setSending] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [enabled, setEnabled] = useState(null); // null = unknown yet
  const [devCode, setDevCode] = useState(''); // simulate-mode code surfaced for dev login
  const timer = useRef(null);

  const L = (key) => (i18n ? i18n(key) : DEFAULT_LABELS[key]);
  const purpose = mode === 'register' ? 'register' : mode === 'bind' ? 'bind' : 'login';

  useEffect(() => {
    let cancelled = false;
    fetchPhoneAuthConfig()
      .then((cfg) => !cancelled && setEnabled(Boolean(cfg?.enabled)))
      .catch(() => !cancelled && setEnabled(false));
    return () => {
      cancelled = true;
      if (timer.current) clearInterval(timer.current);
    };
  }, []);

  useEffect(() => {
    if (cooldown <= 0) {
      if (timer.current) clearInterval(timer.current);
      return;
    }
    timer.current = setInterval(() => setCooldown((c) => Math.max(0, c - 1)), 1000);
    return () => timer.current && clearInterval(timer.current);
  }, [cooldown]);

  if (enabled === false) return null;
  if (enabled === null) return <div className="phone-auth phone-auth--loading" />;

  const sendCode = async () => {
    if (!/^1[3-9]\d{9}$/.test(phone.replace(/\D/g, ''))) {
      toast.error(L('phoneInvalid'));
      return;
    }
    setSending(true);
    try {
      const { ok, data } = await requestPhoneCode(phone, purpose);
      if (ok) {
        setCooldown(Number(data?.resendAfterSeconds) || 60);
        toast.success(data?.simulated ? L('codeSimulated') : L('codeSent'));
        // Surface the simulate-mode code so the user can complete the flow
        // without a real SMS delivery.
        if (data?.simulated && data?.code) setDevCode(data.code);
      } else {
        toast.error(data?.error || L('codeFailed'));
      }
    } catch {
      toast.error(L('codeFailed'));
    } finally {
      setSending(false);
    }
  };

  const finish = (token, user) => {
    if (onSuccess) return onSuccess(token, user);
    if (mode === 'bind') {
      toast.success(L('phoneBound'));
      window.location.reload();
      return;
    }
    persistSession(token, user, next);
  };

  const submitCode = async (e) => {
    e.preventDefault();
    if (!code) {
      toast.error(L('codeRequired'));
      return;
    }
    setSubmitting(true);
    try {
      const call =
        mode === 'register'
          ? phoneRegister({ phone, code, password: password || undefined, name: name || undefined })
          : phoneLogin({ phone, code });
      const { ok, data } = await call;
      if (ok && data?.token) {
        finish(data.token, data.user);
      } else {
        toast.error(data?.error || L('invalidCode'));
      }
    } catch {
      toast.error(L('invalidCode'));
    } finally {
      setSubmitting(false);
    }
  };

  const submitPassword = async (e) => {
    e.preventDefault();
    if (!password) {
      toast.error(L('passwordRequired'));
      return;
    }
    setSubmitting(true);
    try {
      const { ok, data } = await phonePasswordLogin({ phone, password });
      if (ok && data?.token) finish(data.token, data.user);
      else toast.error(data?.error || L('invalidPassword'));
    } catch {
      toast.error(L('invalidPassword'));
    } finally {
      setSubmitting(false);
    }
  };

  const submitBind = async (e) => {
    e.preventDefault();
    const token = localStorage.getItem('authToken');
    if (!token) {
      toast.error(L('signInRequired'));
      return;
    }
    if (!code) {
      toast.error(L('codeRequired'));
      return;
    }
    setSubmitting(true);
    try {
      const { ok, data } = await phoneBind({ phone, code }, token);
      if (ok) finish(null, null);
      else toast.error(data?.error || L('bindFailed'));
    } catch {
      toast.error(L('bindFailed'));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="phone-auth">
      {devCode && (
        <div className="phone-auth__devnote">
          Dev mode (SMS not configured): your verification code is <b>{devCode}</b>
        </div>
      )}
      <div className="phone-auth__row">
        <input
          className="phone-auth__input"
          type="tel"
          inputMode="numeric"
          autoComplete="tel"
          placeholder={L('phonePlaceholder')}
          value={phone}
          onChange={(e) => setPhone(e.target.value)}
        />
        <button
          type="button"
          className="phone-auth__code-btn"
          onClick={sendCode}
          disabled={sending || cooldown > 0}
        >
          {cooldown > 0
            ? L('resendIn').replace('{{s}}', String(cooldown))
            : sending
              ? '…'
              : L('sendCode')}
        </button>
      </div>

      <form onSubmit={mode === 'bind' ? submitBind : submitCode}>
        <input
          className="phone-auth__input"
          type="text"
          inputMode="numeric"
          autoComplete="one-time-code"
          placeholder={L('verificationCode')}
          value={code}
          onChange={(e) => setCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
        />

        {mode === 'register' && (
          <>
            <input
              className="phone-auth__input"
              type="text"
              autoComplete="name"
              placeholder={L('name')}
              value={name}
              onChange={(e) => setName(e.target.value)}
            />
            <input
              className="phone-auth__input"
              type="password"
              autoComplete="new-password"
              placeholder={L('passwordOptional')}
              value={password}
              onChange={(e) => setPassword(e.target.value)}
            />
          </>
        )}

        {mode === 'login' && (
          <>
            <input
              className="phone-auth__input"
              type="password"
              autoComplete="current-password"
              placeholder={L('passwordOptional')}
              value={password}
              onChange={(e) => setPassword(e.target.value)}
            />
            <button type="submit" className="phone-auth__submit" disabled={submitting}>
              {submitting ? '…' : L('signInWithCode')}
            </button>
            <button
              type="button"
              className="phone-auth__alt"
              onClick={submitPassword}
              disabled={submitting}
            >
              {L('signInWithPassword')}
            </button>
          </>
        )}

        {mode === 'register' && (
          <button type="submit" className="phone-auth__submit" disabled={submitting}>
            {submitting ? '…' : L('createAccount')}
          </button>
        )}

        {mode === 'bind' && (
          <button type="submit" className="phone-auth__submit" disabled={submitting}>
            {submitting ? '…' : L('bindPhone')}
          </button>
        )}
      </form>
    </div>
  );
};

export default PhoneAuthPanel;
